class ProductQuickView {
  constructor() {
    this.dialog = document.querySelector('#product-quick-view-dialog');
    this.dialogHost = this.dialog?.closest('theme-dialog');
    this.content = document.querySelector('[data-product-quick-view-content]');
    this.title = document.querySelector('[data-product-quick-view-title]');
    this.textTitle = this.content?.dataset.textTitle || '';
    this.textLoading = this.content?.dataset.textLoading || '';
    this.textUnavailable = this.content?.dataset.textUnavailable || '';
    this.cache = new Map();
    this.abortController = null;
    this.activeTrigger = null;
    this.requestId = 0;

    this.handleClick = this.handleClick.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.syncMediaScrollArea = this.syncMediaScrollArea.bind(this);
    document.addEventListener('click', this.handleClick);
    document.addEventListener('product:variant-change', this.handleVariantChange);
    window.addEventListener('resize', this.syncMediaScrollArea);
  }

  handleClick(event) {
    const trigger = event.target.closest('[data-product-quick-view-url]');

    if (!trigger) {
      return;
    }

    event.preventDefault();
    this.load(trigger.dataset.productQuickViewUrl, trigger);
  }

  async load(productUrl, trigger = null) {
    if (!productUrl || !this.dialog || !this.content) {
      return;
    }

    this.requestId += 1;
    const currentRequestId = this.requestId;
    this.abortController?.abort();
    this.setTriggerLoading(trigger, true);
    this.setModalLoading();

    try {
      const html = await this.fetchSection(productUrl);

      if (currentRequestId !== this.requestId) {
        return;
      }

      this.content.innerHTML = html;
      this.openDialog(trigger);
      this.syncMediaScrollArea();
      this.resetMediaScroll();
      this.refreshOptionIndicators();
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      this.content.innerHTML = `<p class="product-quick-view-error">${this.escapeHTML(this.textUnavailable)}</p>`;
      this.openDialog(trigger);
      console.error('Error loading product quick view:', error);
    } finally {
      if (currentRequestId === this.requestId) {
        this.setTriggerLoading(trigger, false);
      }
    }
  }

  async fetchSection(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', 'product-quick-view');
    const cacheKey = url.toString();

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    this.abortController = new AbortController();

    const response = await fetch(url, {
      signal: this.abortController.signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const html = await response.text();
    this.cache.set(cacheKey, html);
    return html;
  }

  openDialog(trigger = null) {
    if (this.dialog.open) {
      return;
    }

    if (this.dialogHost) {
      this.dialogHost.lastTrigger = trigger;
      this.dialogHost.openDialog();
      this.dialog.addEventListener('transitionstart', this.syncMediaScrollArea, { once: true });
      return;
    }

    if (typeof this.dialog.showModal === 'function' && !this.dialog.open) {
      this.dialog.showModal();
      this.syncMediaScrollArea();
    }
  }

  setTriggerLoading(trigger, isLoading) {
    if (isLoading && this.activeTrigger && this.activeTrigger !== trigger) {
      this.setTriggerLoading(this.activeTrigger, false);
    }

    if (!trigger) {
      return;
    }

    trigger.classList.toggle('loading', isLoading);

    if (isLoading) {
      trigger.setAttribute('aria-busy', 'true');
      this.activeTrigger = trigger;
      return;
    }

    trigger.removeAttribute('aria-busy');

    if (this.activeTrigger === trigger) {
      this.activeTrigger = null;
    }
  }

  setModalLoading() {
    if (this.title) {
      this.title.textContent = this.textTitle;
    }

    this.content.innerHTML = `
      <div
        class="product-quick-view-status"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        ${this.escapeHTML(this.textLoading)}
      </div>
    `;
  }

  refreshOptionIndicators() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.content.querySelectorAll('product-options').forEach((productOptions) => {
          if (typeof productOptions.updateActiveButtonIndicators === 'function') {
            productOptions.updateActiveButtonIndicators();
          }
        });
      });
    });
  }

  handleVariantChange(event) {
    const section = this.content?.querySelector('[data-product-quick-view-section]');
    const sectionId = section?.dataset.sectionId;
    const eventSectionId = event.detail?.sectionId;

    if (!section || (sectionId && eventSectionId && sectionId !== String(eventSectionId))) {
      return;
    }

    this.updateFeaturedImage(event.detail?.variant);
  }

  updateFeaturedImage(variant) {
    if (!variant?.featured_image_800) {
      return;
    }

    const wrapper = this.content?.querySelector('[data-product-quick-view-featured-image]');
    const image = wrapper?.querySelector('.product-quick-view-image');

    if (!wrapper || !image) {
      return;
    }

    const srcset = [
      variant.featured_image_400 && `${variant.featured_image_400} 400w`,
      variant.featured_image_800 && `${variant.featured_image_800} 800w`,
    ].filter(Boolean).join(', ');

    wrapper.dataset.imageId = variant.featured_image_id || '';
    image.src = variant.featured_image_800;
    image.srcset = srcset;
    image.alt = variant.featured_image_alt || '';

    if (variant.featured_image_width) {
      image.width = Number(variant.featured_image_width);
    }

    if (variant.featured_image_height) {
      image.height = Number(variant.featured_image_height);
    }
  }

  syncMediaScrollArea() {
    if (window.matchMedia('(max-width: 599px)').matches) {
      this.content?.querySelector('.product-quick-view-media')?.style.removeProperty('height');
      return;
    }

    const body = this.dialog?.querySelector('.dialog-body');
    const media = this.content?.querySelector('.product-quick-view-media');

    if (!body || !media) {
      return;
    }

    const height = body.clientHeight || this.dialog.clientHeight;
    if (height > 0) {
      media.style.height = `${height}px`;
    }
  }

  resetMediaScroll() {
    const media = this.content?.querySelector('.product-quick-view-media');
    if (media) {
      media.scrollTo({ top: 0, behavior: 'instant' });
    }
  }

  escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

if (!window.productQuickView) {
  window.productQuickView = new ProductQuickView();
}
